import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SectionNode from './SectionNode';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './Outline.css';

export function SectionTree({
  sections,
  selectedSection,
  onSelectSection,
  onReorder
}) {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const breakpoint = useBreakpoint();
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(
    ...(breakpoint !== 'mobile' ? [pointerSensor, keyboardSensor] : [])
  );

  const toggleExpand = (sectionId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // Find indices and reorder
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(sections, oldIndex, newIndex);
        onReorder(newOrder.map(s => s.id), null);
      }
    }
  };

  const renderSection = (section, depth = 0) => {
    const isExpanded = expandedIds.has(section.id);
    const hasSubSections = section.sub_sections && section.sub_sections.length > 0;

    return (
      <React.Fragment key={section.id}>
        <SectionNode
          section={section}
          isSelected={selectedSection?.id === section.id}
          onSelect={onSelectSection}
          onExpand={toggleExpand}
          isExpanded={isExpanded}
          depth={depth}
        />
        {hasSubSections && isExpanded && (
          <div className="section-tree__sub-sections">
            {section.sub_sections.map(sub => renderSection(sub, depth + 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  if (!sections || sections.length === 0) {
    return (
      <div className="section-tree section-tree--empty">
        <p>No sections yet. Add a section to get started.</p>
      </div>
    );
  }

  const sectionIds = sections.map(s => s.id);

  return (
    <div className="section-tree">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
          {sections.map(section => renderSection(section))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default SectionTree;
